/**
 * The Equipes screen (platform design §5.2.1; Figma `315:11169` cards, `320:11540` tree,
 * `329:12603` blocked): the campaign's teams, read through the same `useStructure` query
 * the landed `Tree` already draws, toggled between the nested Cards hierarchy and the Árvore
 * tree.
 *
 * The Figma tree frame is a horizontal, connector-drawn org chart with its own path
 * breadcrumb; the landed `Tree` component draws a vertical nested accordion instead, reused
 * here rather than rebuilt to match -- this task rebuilds Cards over the same structure read,
 * not Árvore.
 *
 * The level path and the unassigned-accounts link sit in the header row beside the view toggle,
 * and each root team is its own card with no card around them all (owner ruling, 2026-09-27,
 * over the frame's separate level-bar card).
 *
 * "Estrutura" in the same Figma header opens the structure dialog (`StructureDialog`) over
 * this page -- it is already its own trigger button and dialog, self-gated on
 * `control_plane.write`, the same split `NewTeam` makes, and mounts directly here rather
 * than behind a route.
 */
import { LayoutGrid, Share2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Page, PageHeader, PageSection } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { useCan } from '../../session/orgContext';
import { t } from '../i18n';
import { LevelSummary } from './LevelSummary';
import { NewTeam } from './NewTeam';
import { NoStructure } from './NoStructure';
import { StructureDialog } from './StructureDialog';
import { buildTeamCardTree, TeamCard } from './TeamCard';
import { useStructure } from './hooks';
import type { TeamFiguresOut } from './schemas';
import { Tree } from './Tree';

type View = 'cards' | 'tree';

function TeamsCards(props: { campaignId: string; teams: TeamFiguresOut[] }): ReactNode {
  if (props.teams.length === 0) {
    return (
      <EmptyState
        title={t('teams.list.empty.title')}
        description={t('teams.list.empty.description')}
      />
    );
  }
  const roots = buildTeamCardTree(props.teams);
  return (
    <div role="group" aria-label={t('teams.cards.label')} className="flex flex-col gap-3">
      {roots.map((root) => (
        <TeamCard key={root.id} campaignId={props.campaignId} node={root} />
      ))}
    </div>
  );
}

export function Teams(props: { campaignId: string; onCreateFirstLevel: () => void }): ReactNode {
  const { campaignId, onCreateFirstLevel } = props;
  const query = useStructure(campaignId);
  const canCreate = useCan('control_plane.write');
  const [view, setView] = useState<View>('cards');
  // `TabsList` and `TabsContent` come from the same `Tabs` root and must appear or vanish
  // together: a trigger for a tab with no matching content behind it points `aria-controls`
  // at an id nothing renders (axe: aria-valid-attr-value), which is exactly what happened
  // while the toggle sat in the header unconditionally and the content only mounted once
  // `useStructure` resolved.
  const hasLevels = query.status === 'success' && query.data.levels.length > 0;

  return (
    <Tabs value={view} onValueChange={(next) => setView(next as View)}>
      <Page>
        <PageHeader
          title={t('teams.title')}
          badge={
            hasLevels ? (
              <LevelSummary
                campaignId={campaignId}
                levels={query.data.levels}
                unassignedAccounts={query.data.unassigned_accounts}
              />
            ) : undefined
          }
          actions={
            hasLevels ? (
              <div className="flex flex-wrap items-center gap-2">
                <TabsList>
                  <TabsTrigger value="cards">
                    <LayoutGrid aria-hidden className="size-4" />
                    {t('teams.view.cards')}
                  </TabsTrigger>
                  <TabsTrigger value="tree">
                    <Share2 aria-hidden className="size-4" />
                    {t('teams.view.tree')}
                  </TabsTrigger>
                </TabsList>
                <StructureDialog campaignId={campaignId} />
                <NewTeam campaignId={campaignId} />
              </div>
            ) : undefined
          }
        />
        <PageSection card={false}>
          <QueryStates
            query={query}
            isEmpty={(data) => data.levels.length === 0}
            empty={
              <NoStructure
                campaignId={campaignId}
                canCreate={canCreate}
                onCreateFirstLevel={onCreateFirstLevel}
              />
            }
          >
            {(data) => (
              <>
                <TabsContent value="cards">
                  <TeamsCards campaignId={campaignId} teams={data.teams} />
                </TabsContent>
                <TabsContent value="tree">
                  <Tree structure={data} campaignId={campaignId} embedded />
                </TabsContent>
              </>
            )}
          </QueryStates>
        </PageSection>
      </Page>
    </Tabs>
  );
}
